import type { Contract } from '~/data/schemas/contract';
import type { Invoice } from '~/data/schemas/invoice';

export interface CoveringContract {
  contract: Contract;
  /** Names of this invoice's elevators that the contract covers. */
  coveredCarNames: string[];
  isGoverning: boolean;
}

/**
 * The contracts relevant to an invoice: the one the backend resolved as governing it,
 * plus any covering an elevator it serviced. Split out of `ContractsSection` so the
 * Related tab can count them without mounting (and fetching) the section.
 */
export function selectCoveringContracts(
  contracts: Contract[],
  invoice: Invoice,
): CoveringContract[] {
  const propertyId = invoice.property?.id;
  if (!propertyId) return [];
  return selectContractsCoveringCars(contracts, {
    propertyId,
    carIds: invoice.carsServiced.map((car) => car.id),
    governingContractId: invoice.activeContractId,
  });
}

/**
 * The contracts on a property covering any of `carIds`. With `wholeProperty`, every
 * contract on the property counts, since nothing narrows it to particular elevators.
 */
export function selectContractsCoveringCars(
  contracts: Contract[],
  {
    propertyId,
    carIds,
    governingContractId,
    wholeProperty = false,
  }: {
    propertyId: string;
    carIds: readonly string[];
    governingContractId?: string | null;
    wholeProperty?: boolean;
  },
): CoveringContract[] {
  const wanted = new Set(carIds);

  return (
    contracts
      .map((contract) => {
        const onProperty = contract.propertyContracts.filter(
          (propertyContract) => propertyContract.propertyId === propertyId,
        );
        const coveredCarNames = onProperty
          .flatMap((propertyContract) => propertyContract.cars)
          .filter((car) => wholeProperty || wanted.has(car.carId))
          .map((car) => car.name);

        return {
          contract,
          coveredCarNames,
          isGoverning: Boolean(governingContractId) && contract.id === governingContractId,
          onProperty: onProperty.length > 0,
        };
      })
      // Keep a governing contract even when its elevator coverage doesn't overlap:
      // the backend resolved it as the one that applied, and hiding it would leave
      // "priced against contract X" with no X anywhere on the page.
      .filter(
        (entry) =>
          entry.isGoverning ||
          entry.coveredCarNames.length > 0 ||
          (wholeProperty && entry.onProperty),
      )
      .sort((a, b) => Number(b.isGoverning) - Number(a.isGoverning))
      .map(({ onProperty: _onProperty, ...entry }) => entry)
  );
}
